import { createRouter, createWebHashHistory } from 'vue-router'
import { ROOT_ROUTE_NAME, ERROR_ROUTE_NAMES, LOGIN_ROUTE_NAME } from '../../consts'
import { getPageKey, _ } from '../../utils'
import { pruneCachedPage, processAppInitialLocation, checkIsHomePage } from './util'

import type { Router } from 'vue-router'
import type { AppRouterConfig, PageInfoData } from '../../typings'
import type { App } from '../App'

export { processAppInitialLocation, checkIsHomePage }

export function createAppRouter(app: App, config?: AppRouterConfig) {
  const _router: Router = config?.router
    ? config.router
    : createRouter({
        history: createWebHashHistory(),
        routes: config?.routes || []
      })

  if (!_router.__cachedNodes) {
    _router.__cachedNodes = {}
  }

  /** 跳转至首页 */
  if (!_router.goHome) {
    _router.goHome = async () => {
      await app.stores.appStore.changeSubmodule({ name: '' })

      return await _router.push({ name: ROOT_ROUTE_NAME })
    }
  }

  /** 跳转至登录页 */
  if (!_router.goLogin) {
    _router.goLogin = async () => {
      return await _router.push({ name: LOGIN_ROUTE_NAME })
    }
  }

  /** 跳转至特定页面 */
  if (!_router.goto) {
    _router.goto = async (to: any) => {
      if (!to) {
        throw new Error('跳转目的地不能为空')
      }

      if (typeof to === 'string') {
        to = { path: to }
      }

      if (typeof to.path === 'string') {
        if (to.path.startsWith('http:') || to.path.startsWith('https:')) {
          window.open(to.path)
          return
        }

        if (to.path.startsWith('/http:') || to.path.startsWith('/https:')) {
          window.open(to.path.slice(1))
          return
        }
      }

      if (to.name) {
        // 获取模版路由
        const tmplRoute = _router.getRouteByName(to.name)
        if (tmplRoute?.meta?.menu) {
          const tmplData = app.deepFilter(tmplRoute?.meta?.menu, to.data || {})
          to = { ...tmplData, ...to }
        }
      }

      let refererKey = to.refererKey
      const currentRoute = _router.currentRoute
      if (!refererKey) {
        refererKey = currentRoute.value.meta?.pageKey as string
      }

      if (refererKey) {
        to.meta = { ...to.meta, refererKey }
      }

      const pageKey = getPageKey(to)

      // 防止死循环
      if (pageKey === refererKey) return

      const keeyAlive = app.useAppConfig('page.keeyAlive')
      const { pagesStore, appStore } = app.stores

      if (!keeyAlive) {
        if (!refererKey) {
          // 没有引用页面则直接清空
          await pagesStore.pruneCurrentVisited({
            submodule: 'ALL',
            redirect: false
          })
        } else if (currentRoute.value?.meta) {
          // 将当前页加入缓存页
          await pagesStore.addCurrentVisited({ redirect: false })
        }
      }

      // 判断是否存在onlyPath，如果存在，则只根据path生成，不然会根据path+query生成
      let toRoute: any
      if (to.onlyPath) {
        let routes = _router.getRoutes()
        let _toRoute: any = routes.find(it => it.meta?.path === to.path)

        const _toPage = pagesStore.visited.find(it => it.key === _toRoute?.meta.pageKey)
        // 如果_toRoute存在，把to的query内容替换掉
        if (_toPage) {
          if (pageKey === _toPage.key) {
            toRoute = _router.getRouteByPageKey(pageKey)
          } else {
            if (_toPage.isTemp) {
              // 如果页面缓存存在，则删除缓存，再添加新的（解决只记录第一次的缓存，后续的跳转没记录，导致切换tab时，会回到第一次的路由问题）
              await pagesStore.removeTemp(_toPage, false)
              await pagesStore.addTemp(to)

              toRoute = _router.getRouteByPageKey(pageKey)
            } else {
              // 去掉移除逻辑
              // await pagesStore.removeVisited(_toPage)
              toRoute = _toRoute
            }
          }
        } else {
          // 如果_toRoute不存在，直接赋值
          toRoute = _toRoute
        }
      } else {
        toRoute = _router.getRouteByPageKey(pageKey)
      }

      // 若路由不存在则新增路由
      if (!toRoute) {
        to.submodule = to.submodule || appStore.navMenu?.submodule
        // 如果title在url中不存在，则跳到首页
        if (!to.title) {
          return await _router.goHome()
        }

        // 当前路由不存在，则新增临时路由
        await pagesStore.addTemp(to)

        return await _router.push(to)
      } else {
        return await _router.push({ ...toRoute.meta, query: to.query })
      }
    }
  }

  /** 返回 */
  if (!_router.goBack) {
    _router.goBack = async () => {
      const route = _router.currentRoute
      const refererKey = route.value.meta?.refererKey as string

      if (refererKey) {
        const refererRoute = _router.getRouteByPageKey(refererKey)
        const rerfereMeta = refererRoute?.meta

        if (rerfereMeta?.name) {
          return await _router.push({
            name: rerfereMeta.name as string,
            query: rerfereMeta.query as any
          })
        }
      }

      return _router.go(-1)
    }
  }

  if (!_router.close) {
    _router.close = async (pageKey?: string) => {
      const { pagesStore } = app.stores

      const showNav = app.useAppConfig('menu.showNav')

      if (!showNav) {
        _router?.goBack()
      } else {
        const route = _router.currentRoute.value
        pageKey = (pageKey || route?.meta?.pageKey) as string

        if (!pageKey) return

        await pagesStore.removeCurrentVisited({ key: pageKey })
      }
    }
  }

  /** 根据名称获取指定的路由 */
  if (!_router.getRouteByName) {
    _router.getRouteByName = (name: string) => {
      const routes = _router.getRoutes()
      const route = routes.find(it => it.name === name)
      return route
    }
  }

  if (!_router.getCurrentPageKey) {
    _router.getCurrentPageKey = () => {
      const route = _router.currentRoute
      return route.value?.meta?.pageKey as string
    }
  }

  /** 根据PageKey获取指定的路由 */
  if (!_router.getRouteByPageKey) {
    _router.getRouteByPageKey = (key: string) => {
      if (!key) return undefined

      const routes = _router.getRoutes()
      const route = routes.find(it => it.meta?.pageKey === key)
      return route
    }
  }

  /** 根据PageKey获取指定的路由 */
  if (!_router.getRouteByMenuPath) {
    _router.getRouteByMenuPath = (menuPath: string) => {
      if (!menuPath) return undefined

      const routes = _router.getRoutes()
      const route = routes.find(it => it.meta?.menuPath === menuPath)
      return route
    }
  }

  /** 清理当前页面缓存 */
  if (!_router.pruneCurrentPage) {
    _router.pruneCurrentPage = () => {
      const route = _router.currentRoute?.value
      if (!route?.meta) return

      pruneCachedPage(_router, route.meta)
    }
  }

  if (config?.beforeResolve) {
    _router.beforeResolve(config?.beforeResolve)
  }

  if (config?.onError) {
    _router.onError(config?.onError)
  }

  if (config?.beforeEach) {
    _router.beforeEach(config?.beforeEach)
  } else {
    _router.beforeEach(async (to: any, from, next) => {
      if (to.name === LOGIN_ROUTE_NAME) {
        return next()
      }

      if (!from.name) {
        if (_.isString(to.name) && [...ERROR_ROUTE_NAMES].includes(to.name)) {
          return next()
        }

        if (to.meta?.isTemp) {
          const parentName = to.meta?.parentName as string
          if (parentName) {
            // TODO: 菜单路由有问题
            if (!_router.getRouteByName(parentName)) {
              return next()
            }

            return next({ name: parentName, replace: true })
          } else {
            return next({ name: ROOT_ROUTE_NAME, replace: true })
          }
        } else if (!to.meta?.redirectQuery) {
          // vue router的match只关注path，这里通过path和query进行匹配
          const menuPath = location.hash.substring(1)
          const route = _router.getRouteByMenuPath(menuPath)
          if (route?.name && route.name !== to.name) {
            return next({ name: route.name, query: to.query, replace: true })
          }
        }
      }

      if (to.meta?.redirectQuery) {
        const query = Object.assign({}, to.query, to.meta?.redirectQuery)

        if (!_.deepEqual(query, to.query)) {
          return next({ name: to.name as string, query, replace: true })
        }
      }

      // 现在所有tab全部展开，不需要切换subModule
      if (to.meta?.submodule && !to.meta?.isSingle) {
        await app.stores.appStore.changeSubmodule({
          name: to.meta?.submodule,
          to
        })
      }

      // pageKey
      const fromPageKey = getPageKey(from)
      const toPageKey = getPageKey(to)
      // console.log('from ===', from)
      // console.log('to ===', to)

      // 页面进入
      app.emits('trace', { event_code: 'pageIn', event_data: { from, to } })

      // 页面离开
      app.emits('trace', { event_code: 'pageOut', event_data: { from, to } })

      return next()
    })
  }

  if (config?.afterEach) {
    _router.afterEach(config?.afterEach)
  } else {
    _router.afterEach(async (to, from) => {
      const keeyAlive = app.useAppConfig('page.keeyAlive')
      const { pagesStore } = app.stores

      await pagesStore.setCurrent((to.meta?.pageKey || to.name) as string, to.meta?.submodule as string)

      // 移除临时路由
      if (keeyAlive === false && from.name && from.meta?.isTemp) {
        await pagesStore.removeTemp({ ...from.meta, name: from.name } as PageInfoData)
      }
    })
  }

  return _router
}
